import { useEffect, useState } from 'react';

import { faAngleDown, faSliders } from '@fortawesome/pro-light-svg-icons';
import { PlanningEntityType } from 'generated';
import { useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';

import { ViewContext, View } from '../../enums';

import { GRAY, GRAY_LIGHT_SECONDARY, TEXT } from 'constants/colors';

import { getViewContext } from '../../utils';

import { Box, Menu, MenuItem, SxProps } from '@mui/material';

import { FontAwesomeSvgIcon } from 'components/shared';

import usePlanningEntity, {
  PlanningEntityTypePreference
} from '../../hooks/usePlanningEntity';

interface Props {
  view: View;
  disabled?: boolean;
  sx?: SxProps;
}

export default function EntityMenu({ view, disabled, sx }: Props) {
  const {
    hasWorkPlacePlanningViewAccess,
    hasMyActivitiesProjectAccess,
    hasManageOwnProjectsAccess,
    hasActivityPlanningViewAccess,
    hasKidsPlanAccess,
    hasProjectTeamAccess
  } = useSelector(({ grants }) => grants);
  const { t } = useTranslation('common', {
    keyPrefix: 'activityPlanning.planningEntityType'
  });
  const [entity, setEntity] = usePlanningEntity();
  const selectedEntity = entity[view];

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  let planningEntityTypes: PlanningEntityType[] = [];
  const context = getViewContext();
  if (
    (hasMyActivitiesProjectAccess && context === ViewContext.Own) ||
    ((hasManageOwnProjectsAccess || hasProjectTeamAccess) &&
      context === ViewContext.Team &&
      view != View.Timeline)
  ) {
    planningEntityTypes.push('PROJECT');
  }
  if (hasWorkPlacePlanningViewAccess && view != View.Timeline) {
    planningEntityTypes.push('WORKPLACE');
  }
  if (hasActivityPlanningViewAccess && view == View.Timeline) {
    planningEntityTypes.push('ROUTE');
  }
  if (
    hasActivityPlanningViewAccess ||
    hasKidsPlanAccess ||
    (hasProjectTeamAccess && view === View.List)
  ) {
    planningEntityTypes.push('EMPLOYEE');
  }
  if (hasActivityPlanningViewAccess && view != View.Timeline) {
    planningEntityTypes.push('CLIENT');
  }

  // Only Roster and List view when viewing My Activities
  if (context === ViewContext.Own) {
    planningEntityTypes = planningEntityTypes.filter(
      (t: PlanningEntityType) => t === 'WORKPLACE' || t === 'PROJECT'
    );
  }

  useEffect(() => {
    if (!planningEntityTypes.includes(selectedEntity)) {
      setEntity({
        [view]: planningEntityTypes[0]
      } as PlanningEntityTypePreference);
    }
  }, []);

  return (
    <>
      <Box
        component="button"
        onClick={handleClick}
        height="40px"
        display="flex"
        alignItems="center"
        bgcolor={disabled ? '#fcfcfc' : '#f7f7f7'}
        border={`1px solid ${GRAY_LIGHT_SECONDARY}`}
        padding="0 16px"
        justifyContent="space-between"
        sx={{
          cursor: disabled ? 'auto' : 'pointer',
          transitionDuration: '150ms',
          position: 'relative',
          top: 0,
          left: 0,
          zIndex: 31,
          ...(sx || {})
        }}
        py={2}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          fontFamily="signikasemibold"
          fontSize={13}
          color={disabled ? GRAY : TEXT}
          sx={{
            opacity: disabled ? 0.5 : 1
          }}
        >
          <FontAwesomeSvgIcon icon={faSliders} fontSize="small" />
          {t(entity[view]).toUpperCase()}
        </Box>
        {!disabled && (
          <FontAwesomeSvgIcon
            sx={{ ml: 1 }}
            icon={faAngleDown}
            fontSize="small"
          />
        )}
      </Box>
      {!disabled && (
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          {planningEntityTypes.map((entity: PlanningEntityType, i: number) => (
            <MenuItem
              key={i}
              onClick={() => {
                setEntity({ [view]: entity } as PlanningEntityTypePreference);
                handleClose();
              }}
            >
              {t(entity)}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
}
